/**
 * Relacion inversa (`one2many`) como grilla editable en linea.
 *
 * Sobre un registro YA GUARDADO la grilla no acumula cambios: crea y modifica
 * los registros hijos directamente contra su propio modelo, linea por linea, que
 * es lo que corresponde porque cada hijo tiene sus propios permisos y sus
 * propias reglas de fila.
 *
 * Sobre un registro que TODAVIA NO EXISTE (`context.recordId === null`) hace lo
 * otro: las filas se acumulan en memoria —viajan por el `value`/`onChange` del
 * campo, como cualquier otro widget— y las persiste el `FormView` junto con la
 * cabecera, en el mismo POST y por lo tanto en **una sola transaccion**
 * (`_create_child_rows` en `core/api.py`).
 *
 * Antes esta pantalla decia «Guardá el registro para poder cargar renglones» y
 * no dibujaba nada. El motivo tecnico era cierto —la fila hija necesita el id
 * del padre para escribir su FK— pero la conclusion no: no hace falta que el
 * usuario guarde primero, hace falta que el padre exista **antes que los hijos
 * dentro de la misma transaccion**, y eso lo resuelve el servidor. Con el cartel,
 * lo primero con lo que se chocaba cualquiera que quisiera cargar una venta era
 * un formulario que le pedia guardar un presupuesto sin un solo renglon.
 *
 * La integridad no se relaja en ningun punto: el POST anidado corre el
 * `check_access` de `create` del modelo hijo, su `full_clean()` y sus reglas de
 * registro, fila por fila, y si una falla no queda escrito NADA —ni la cabecera—.
 *
 * La grilla se puede AJUSTAR DESDE EL BACKEND, sin tocar este archivo, con las
 * `options` del `FieldSpec` del padre (`schema.py`). Son genericas: sirven para
 * cualquier modelo, y este componente no sabe de ninguno en particular.
 *
 *   - `columns`: que columnas mostrar y en que orden. Por defecto, las de la
 *     vista lista del hijo. Existe porque la misma tabla se mira con preguntas
 *     distintas segun donde este embebida: en la ficha de un cliente sobra la
 *     columna "cliente" y falta "vence el".
 *   - `column_labels`: `{campo: etiqueta}` para renombrar un encabezado SOLO en
 *     esta pantalla, sin cambiarle el nombre al campo en todo el ERP.
 *   - `order`: con que orden se piden las lineas (el mismo string que `?order=`).
 *     Es el orden INICIAL: el usuario puede cambiarlo clickeando un encabezado,
 *     igual que en la pantalla completa del modelo hijo. Se reordena en el
 *     servidor (`?order=`) y no en el cliente, asi que ordenar por «Vence el»
 *     ordena las 100 lineas que trae la grilla y no solo las visibles, y lo
 *     nullable queda al final por el `NULLS LAST` de la API.
 *   - `domain`: terminos que se suman (con AND) al de la relacion. Sirve para que
 *     la grilla pida lo MISMO que cuenta el boton inteligente de arriba cuando el
 *     conteo es cross-empresa: sin el, la solapa «Consorcios» de una administracion
 *     mostraba 1 linea mientras el boton decia 6.
 *   - `filters`: nombres de filtros de la vista BUSQUEDA del hijo que se dibujan
 *     como chips de un clic arriba de la grilla. El dominio del filtro se suma
 *     al del padre, asi que filtrar no puede sacar lineas de otro registro.
 *   - `empty_text`: que decir cuando no hay ninguna linea. Por defecto "Todavia
 *     no hay <etiqueta>", que con etiquetas que son una pregunta suena raro.
 *   - `readonly_domain` / `readonly_text`: bloqueo CONDICIONAL segun el estado del
 *     registro padre. Lo resuelve `FormView` (que es quien tiene los valores del
 *     padre en la mano) y llega aca como el prop `readonly`; `readonly_text` se
 *     dibuja arriba de la grilla para que se sepa POR QUE no se puede editar.
 *     Caso testigo: los gastos de un periodo de expensas ya emitido.
 *   - `readonly`: la grilla se LEE, no se edita en linea. Es para las relaciones
 *     que escribe un proceso y no una persona —lo vendido lo escribe la factura
 *     al confirmarse— donde una celda editable invita a que la ficha diga una
 *     cosa y el comprobante otra. Cada fila se sigue abriendo con su flecha,
 *     que es donde se la trabaja de verdad (renovar, dar de baja, anotar un
 *     llamado). Ademas el texto entra completo: sin `<input>` de por medio, la
 *     columna se ajusta al contenido en vez de recortar el nombre del producto.
 */

import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { FieldDisplay } from '@/components/fields/FieldDisplay';
import { toDraftRows } from '@/components/fields/format';
import { getFieldInput } from '@/components/fields/registry';
import type { FieldInputProps } from '@/components/fields/types';
import { IconChevronRight, IconClose, IconSpinner } from '@/components/shell/icons';
import { recordPath } from '@/components/shell/menu-utils';
import { useMetaQuery } from '@/components/shell/queries';
import { notify, useToastStore } from '@/components/shell/toast';
import { Button } from '@/components/shell/primitives';
import { ApiError, api } from '@/lib/api';
import type {
  Domain,
  ListParams,
  DomainNode,
  FieldMeta,
  FieldValue,
  ModelSpec,
  RecordData,
  RecordPayload,
} from '@/lib/types';
import { useInvalidateModel, useRecordsQuery } from '@/views/queries';
import { AvisoDeBloqueo } from '@/views/MotivoBloqueo';
import { validateField } from '@/views/validation';
import type { OrderState } from '@/views/view-utils';
import { parseOrder, serializeOrder, toggleOrder } from '@/views/view-utils';

/**
 * Lineas por pagina.
 *
 * Antes eran 100 FIJAS y sin ningun control: la grilla pedia las primeras cien y
 * lo que sobraba no existia. Torre Rivadavia tiene 12 unidades por 5 o 6 rubros
 * —unas 70 filas— y entraba raspando; un consorcio de 60 unidades se cortaba en
 * la mitad y el usuario no tenia forma de enterarse. Ahora la grilla pagina y
 * SIEMPRE dice cuantas de cuantas esta mostrando.
 */
const PAGINA = 50;

/**
 * Ancho de una columna segun QUE tipo de dato lleva. La regla es GENERICA — por
 * tipo de campo, no por modelo — y la aplica `table-layout: fixed`.
 *
 * El problema que resuelve: con el reparto automatico del navegador, el ancho de
 * cada columna sale del CONTENIDO del encabezado y de las celdas. «Importe»
 * —siete letras y un input que no reclama nada porque es `w-full`— se quedaba
 * con **97 px (73 para el input)**, mientras «Detalle» y «Proveedor» se llevaban
 * media pantalla. Es exactamente al reves de lo que uno mira: la plata y las
 * fechas se leen enteras o no sirven; el texto se puede recortar.
 *
 * Los tipos de acá reciben un ancho FIJO, calculado sobre el peor caso real:
 *
 *   - `money`: 11rem. El simbolo va DENTRO del input (`pl-10`), asi que un
 *     importe de siete digitos —«$ 2.094.870,50»— no entra en menos.
 *   - `date`: 8.5rem, que es `dd/mm/aaaa` mas el boton del calendario.
 *   - los numeros, los booleanos y los desplegables, lo que pide su contenido.
 *
 * El texto libre y los many2one tambien declaran ancho, y no por prolijidad:
 * con `table-layout: fixed`, una columna SIN ancho se queda con lo que sobre, y
 * en una grilla de once columnas —los renglones de un pedido— eso es cero. Se
 * les da una base generosa; si la suma no entra, la grilla se desplaza en
 * horizontal, que es la respuesta honesta a "once columnas no caben en 1100 px".
 * Si sobra espacio, el navegador lo reparte proporcionalmente entre todas.
 */
const ANCHO_DE_COLUMNA: Record<string, string> = {
  money: '11rem',
  monetary: '11rem',
  date: '8.5rem',
  datetime: '11rem',
  time: '7rem',
  percent: '7.5rem',
  area: '7.5rem',
  hours: '7rem',
  integer: '7rem',
  decimal: '8rem',
  float: '8rem',
  boolean: '4.5rem',
  switch: '4.5rem',
  checkbox: '4.5rem',
  selection: '9rem',
  badge: '9rem',
  status: '9rem',
  color: '6rem',
  // El selector de relacion gasta ~64 px en sus dos botones (abrir y quitar)
  // antes de mostrar una letra del nombre, asi que necesita mas que un texto.
  many2one: '12rem',
  many2many: '12rem',
  char: '11rem',
  text: '11rem',
  email: '11rem',
  url: '11rem',
};

/** Base de las columnas cuyo tipo no figura en la tabla. */
const ANCHO_POR_DEFECTO = '10rem';

/**
 * Ancho de la ULTIMA columna, la de los botones de cada fila.
 *
 * Era `w-28` (7rem = 112 px) siempre, y con `table-layout: fixed` ese ancho se
 * respeta al pixel: la celda NO crece con su contenido. Los botones de la fila
 * de carga —«Agregar» (75 px) y «Cancelar» (78 px), mas el `gap` y el `px-3`—
 * necesitan ~181 px, asi que sobraban ~70 px que el `flex justify-end` empujaba
 * hacia la IZQUIERDA, encima de la columna anterior. Medido en la grilla de
 * gastos a 1440 px: la celda de acciones iba de x=1263 a x=1380 y el boton
 * «Agregar» arrancaba en x=1210, tapando de 1210 a 1251 el input de «Importe»
 * (que termina en 1251). Es decir: los botones dibujados ENCIMA de la celda
 * donde se escribe plata, justo sobre su borde derecho.
 *
 * Se declaran los dos anchos por separado porque la diferencia es real: una
 * grilla de solo lectura lleva dos iconos de 32 px y pedirle 12rem seria
 * quitarle espacio a los datos.
 */
const ANCHO_ACCIONES_EDITABLE = '12rem';
const ANCHO_ACCIONES_LECTURA = '7rem';

/**
 * La columna de acciones queda ANCLADA al borde derecho de la grilla.
 *
 * Con siete columnas —las que necesita cargar un gasto de expensas— la tabla no
 * entra en una pantalla de 1280 px y el contenedor se desplaza en horizontal.
 * Sin anclarla, «Agregar» y «Guardar» quedaban FUERA de la vista: para dar de
 * alta la linea que se acaba de tipear habia que desplazar la grilla hasta el
 * final. Anclada, el boton que cierra la fila esta siempre a la vista, y el
 * fondo opaco mas el borde izquierdo hacen que las celdas pasen POR DEBAJO en
 * vez de mezclarse con los botones.
 *
 * Cuando la grilla entra entera, `sticky` no cambia absolutamente nada.
 */
const CLASE_ACCIONES = 'sticky right-0 z-10';

/** Ancho de la columna, por widget declarado y si no por tipo de campo. */
function anchoDeColumna(meta: FieldMeta | undefined): string {
  if (meta === undefined) return ANCHO_POR_DEFECTO;
  return (
    (meta.widget === undefined ? undefined : ANCHO_DE_COLUMNA[meta.widget]) ??
    ANCHO_DE_COLUMNA[meta.type] ??
    ANCHO_POR_DEFECTO
  );
}

/**
 * Campos que el framework completa solo al crear el hijo (el aislamiento por
 * organizacion y por empresa). Aunque un modulo los marque como obligatorios en
 * su `FieldSpec`, el backend los rellena desde el contexto (`X-Company-Id` y el
 * tenant del usuario), asi que NUNCA hay que pedirlos en una alta inline: hacerlo
 * mostraria un selector de empresa vacio en la grilla de abonos de un contacto.
 */
const CAMPOS_AUTOCOMPLETADOS = new Set(['company', 'tenant']);

type Borrador = Record<string, FieldValue>;

/** Lee una opcion del `FieldSpec` como lista de strings; `[]` si no esta o no lo es. */
function opcionLista(meta: FieldMeta, clave: string): string[] {
  const valor = meta.options?.[clave];
  if (!Array.isArray(valor)) return [];
  return valor.filter((entrada): entrada is string => typeof entrada === 'string');
}

/**
 * Terminos extra que el padre le suma al dominio de la grilla (`options.domain`).
 *
 * Existe por un caso concreto: la solapa «Consorcios» de la ficha del
 * administracion. La grilla se pide con `[[campoInverso, '=', padreId]]`, ese
 * dominio no habla de la empresa, y entonces la API lo acota a la empresa activa
 * del conmutador. Resultado: el boton inteligente de arriba decia «6» y la
 * grilla de abajo listaba 1. Con `options.domain = [["company","!=",null]]` la
 * grilla pide exactamente lo mismo que cuenta el boton.
 *
 * Se valida la forma antes de usarlo: lo que viene de la metadata es JSON libre.
 */
function opcionDominio(meta: FieldMeta): DomainNode[] {
  const valor = meta.options?.['domain'];
  if (!Array.isArray(valor)) return [];
  const salida: DomainNode[] = [];
  for (const entrada of valor) {
    if (typeof entrada === 'string') salida.push(entrada as DomainNode);
    else if (Array.isArray(entrada) && entrada.length === 3) {
      salida.push([String(entrada[0]), entrada[1] as DomainNode & string, entrada[2]] as DomainNode);
    }
  }
  return salida;
}

/** Lee una opcion del `FieldSpec` como texto; `undefined` si no esta o no lo es. */
function opcionTexto(meta: FieldMeta, clave: string): string | undefined {
  const valor = meta.options?.[clave];
  return typeof valor === 'string' ? valor : undefined;
}

/** El dominio de la grilla: las filas que cuelgan de ESTE padre, más lo que declaró. */
function dominioDelPadre(meta: FieldMeta, padreId: number | null): DomainNode[] {
  return [[meta.relation_field ?? 'id', '=', padreId ?? 0], ...opcionDominio(meta)];
}

/**
 * Los parámetros con los que la grilla pide su PRIMERA página, sin filtro ni
 * orden elegidos por la persona —el estado con el que se monta—, o `null` si
 * esta grilla no pide filas (relación genérica o sin campo inverso).
 *
 * Existe para que la ficha pueda pedir las filas de la grilla EN PARALELO con
 * los datos del registro (ver `ViewSwitcher`): sale la misma clave de caché que
 * va a usar la grilla al montarse, así que cuando se monta ya las tiene. Antes
 * salían en una segunda tanda: medido con la demora real, un segundo más al
 * abrir una venta, un pedido o una factura.
 */
export function parametrosInicialesDeLaGrilla(meta: FieldMeta, padreId: number): ListParams | null {
  if (meta.generic === true || meta.relation_field === undefined || meta.relation === undefined) return null;
  const base = { domain: dominioDelPadre(meta, padreId) as Domain, limit: PAGINA, offset: 0 };
  const order = opcionTexto(meta, 'order');
  return order !== undefined ? { ...base, order } : base;
}

/** `{campo: etiqueta}` declarado por el padre para renombrar encabezados aca. */
function etiquetasDeclaradas(meta: FieldMeta): Record<string, string> {
  const valor = meta.options?.['column_labels'];
  if (valor === null || typeof valor !== 'object' || Array.isArray(valor)) return {};
  const salida: Record<string, string> = {};
  for (const [campo, etiqueta] of Object.entries(valor)) {
    if (typeof etiqueta === 'string') salida[campo] = etiqueta;
  }
  return salida;
}

/**
 * Columnas de la grilla: las que declara el padre en `options.columns` y, si no
 * declara ninguna, las de la vista lista del hijo. Nunca la vuelta al padre.
 */
function columnasDe(
  hijo: ModelSpec,
  campoInverso: string | undefined,
  declaradasPorElPadre: string[],
): string[] {
  const declaradas = declaradasPorElPadre.length > 0
    ? declaradasPorElPadre
    : hijo.views.list?.columns ?? [];
  const candidatas =
    declaradas.length > 0
      ? [...declaradas]
      : Object.entries(hijo.fields)
          .filter(([, meta]) => meta.store && meta.type !== 'json')
          .slice(0, 5)
          .map(([nombre]) => nombre);

  // Un alta inline tiene que poder completar TODO campo obligatorio del hijo. Si
  // uno de esos campos no esta entre las columnas —«Vendido el» de lo vendido es
  // el caso cuando la lista no lo muestra— la grilla no tiene donde pedirlo y
  // el alta falla SIEMPRE contra el backend con «este campo no puede ser nulo»,
  // filtrando ademas el nombre tecnico en ingles. Las columnas de la lista se
  // eligen para MOSTRAR, no para CARGAR; asi que se agregan al final los
  // obligatorios que falten. Se excluyen los que el framework completa solo
  // (empresa/organizacion) y los que ya traen un valor por defecto: ninguno de
  // esos bloquea el alta.
  for (const [nombre, meta] of Object.entries(hijo.fields)) {
    if (candidatas.includes(nombre)) continue;
    if (!meta.required || meta.readonly || meta.editable === false || !meta.store) continue;
    if (nombre === campoInverso || CAMPOS_AUTOCOMPLETADOS.has(nombre)) continue;
    if (meta.default !== undefined && meta.default !== null) continue;
    candidatas.push(nombre);
  }

  return candidatas.filter((nombre) => {
    if (nombre === campoInverso) return false;
    const meta = hijo.fields[nombre];
    // Un x2many anidado dentro de una celda no se puede editar de forma
    // comprensible; la linea se abre en su propio formulario para eso.
    return meta !== undefined && meta.type !== 'one2many' && meta.type !== 'many2many';
  });
}

/**
 * Con qué valores nace una fila nueva: los `default` que declara el hijo.
 *
 * Antes nacía vacía (`{}`) y el backend rellenaba los defaults al crear. Eso
 * dejó de alcanzar cuando la grilla empezó a validar antes de mandar: un campo
 * obligatorio CON default se veía vacío en pantalla y la validación lo marcaba
 * como faltante, cuando en realidad el sistema ya sabía qué poner. Ahora el
 * default se ve en la celda —que además es la información que el usuario
 * necesita para decidir si lo cambia— y el POST manda lo mismo que mandaría el
 * formulario completo del hijo.
 *
 * Lo que NO se siembra es el default de una columna de SOLO LECTURA. Parece un
 * detalle y no lo es: los importes calculados del renglón —«Neto», «Total»,
 * «Facturado»— tienen `default=0.00` en la base, así que la fila nacía con esos
 * tres valores adentro y el alta los mandaba. El backend los rechaza, y con
 * razón: son columnas que él calcula (`_apply_payload` contesta «Es un dato de
 * sólo lectura: no se edita desde acá»). El usuario no había tocado ninguna de
 * las tres —ni podía, se dibujan como texto— y el alta le fallaba nombrándolas.
 */
function valoresDeFilaNueva(hijo: ModelSpec, columnas: string[]): Borrador {
  const salida: Borrador = {};
  for (const nombre of columnas) {
    const metaCampo = hijo.fields[nombre];
    if (metaCampo === undefined) continue;
    if (metaCampo.readonly || metaCampo.editable === false) continue;
    if (metaCampo.default === undefined || metaCampo.default === null) continue;
    salida[nombre] = metaCampo.default as FieldValue;
  }
  return salida;
}

/**
 * Trae a la vista —y enfoca— la celda que el backend o la validacion señalaron.
 *
 * El defecto que arregla: la grilla de gastos de un periodo tiene siete columnas
 * y no entra en una pantalla de 1568 px, asi que se desplaza en horizontal.
 * Cuando el error caia sobre una columna FUERA de la vista, el globo rojo
 * quedaba dibujado donde nadie lo veia y el aviso flotante nombraba una columna
 * invisible: el usuario leia «Grupo de unidades no es un grupo de Torre
 * Rivadavia» sin tener ese campo en pantalla. Un error nunca puede apuntar a un
 * lugar al que el usuario no puede mirar.
 *
 * `focus()` solo no alcanza: los widgets relacionales no son un `<input>` sino
 * un contenedor con `role="group"`, y un contenedor no recibe foco. Por eso se
 * busca el primer control enfocable adentro, y el desplazamiento se pide sobre
 * la celda —`inline: 'center'`, `block: 'nearest'`— para mover la grilla en
 * horizontal sin arrastrar la pagina entera en vertical.
 */
function traerCeldaALaVista(idCelda: string): void {
  const elemento = document.getElementById(idCelda);
  if (elemento === null) return;
  const celda = elemento.closest('td') ?? elemento;
  celda.scrollIntoView({ block: 'nearest', inline: 'center' });
  if (elemento instanceof HTMLElement && elemento.tabIndex >= 0) {
    elemento.focus({ preventScroll: true });
    return;
  }
  const enfocable = elemento.querySelector<HTMLElement>(
    'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])',
  );
  enfocable?.focus({ preventScroll: true });
}

export function One2ManyField({
  id,
  meta,
  value,
  onChange,
  readonly,
  context,
  describedBy,
}: FieldInputProps) {
  const invalidar = useInvalidateModel();
  const relation = meta.relation;
  const campoInverso = meta.relation_field;
  const padreId = context.recordId;
  const padreModelo = context.modelName;
  /**
   * El padre todavia no existe: las filas se acumulan acá y las guarda el
   * `FormView` con la cabecera. Ver el docstring del archivo.
   */
  const esAlta = padreId === null;
  const enMemoria = toDraftRows(value);

  /**
   * Refresca el hijo Y el padre despues de tocar una linea.
   *
   * El padre guarda columnas que se DERIVAN de sus hijos: el pie del pedido
   * (neto / impuestos / total) lo recalcula el backend al guardar un renglon.
   * Invalidando solo el modelo hijo, esos totales seguian mostrando el valor
   * viejo —tipicamente $0,00— hasta que el usuario recargaba con F5, y esa cifra
   * es lo central del formulario de venta. Refrescar tambien el padre hace que el
   * `FormView` vuelva a leer el registro y el pie muestre el numero real.
   */
  const refrescarPadreEHijo = (): void => {
    void invalidar(relation as string);
    if (padreModelo && padreModelo !== relation) void invalidar(padreModelo);
  };

  const [borradores, setBorradores] = useState<Record<number, Borrador>>({});
  const [nuevas, setNuevas] = useState<Array<{ clave: number; valores: Borrador }>>([]);
  const [guardando, setGuardando] = useState<number | null>(null);
  /**
   * Errores por celda de las filas NUEVAS: `{clave de la fila: {campo: mensaje}}`.
   *
   * La grilla validaba nada y mandaba igual, asi que un campo obligatorio sin
   * completar volvia del servidor como «invoice: Este campo no puede ser nulo»
   * en un toast que nombra el campo en INGLES y no marca la celda. Se valida con
   * la misma funcion que el formulario (`validateField`) para no tener dos
   * criterios distintos sobre la misma metadata.
   */
  const [erroresNuevas, setErroresNuevas] = useState<Record<number, Record<string, string>>>({});
  /**
   * Errores del SERVIDOR sobre una fila existente: `{id de la linea: {campo: mensaje}}`.
   *
   * El 400 de un guardado en linea solo salia como aviso flotante, asi que el
   * usuario leia «no se pudo guardar» y tenia que adivinar cual de las seis
   * celdas de la fila estaba mal. Ahora el error se pinta en su celda, con la
   * etiqueta del campo en castellano que manda el propio backend.
   */
  const [erroresExistentes, setErroresExistentes] = useState<Record<number, Record<string, string>>>({});
  /**
   * Aviso flotante rojo que quedo abierto por cada fila: `{fila: id del toast}`.
   *
   * Los avisos de error NO se auto-cierran (`DURACION_POR_TONO.danger = 0` en
   * `toast.ts`), y eso esta bien: si algo fallo, la persona tiene que poder
   * leerlo con calma. Lo que estaba mal era que tampoco se cerraran cuando el
   * problema DEJABA de existir: se corregia la celda, se guardaba, la linea
   * entraba —el pie del padre se actualizaba y todo— y «No se pudo guardar la
   * linea» seguia ahi abajo a la derecha, contradiciendo a la pantalla. El
   * usuario terminaba cerrandolo a mano para saber si le habia tomado el cambio.
   *
   * Asi que cada fila se acuerda del aviso que dejo, y el proximo guardado con
   * exito sobre ESA fila lo retira. Es por fila y no uno global a proposito: si
   * falla la fila 3 y despues se guarda bien la 5, el error de la 3 sigue siendo
   * verdad y tiene que quedarse en pantalla.
   *
   * Va en un `ref` y no en un `state` porque no se dibuja: nadie re-renderiza
   * por esto.
   */
  const avisoDeError = useRef(new Map<string, number>());
  /** Filtro elegido de los que declara el padre. `null` = «Todos». */
  const [filtroActivo, setFiltroActivo] = useState<string | null>(null);
  /**
   * Orden elegido clickeando un encabezado. `null` = todavia no se toco nada y
   * manda el `options.order` que declaro el padre.
   */
  const [orden, setOrden] = useState<OrderState | null>(null);
  /** Pagina visible, base 0. Cambiar de filtro o de orden vuelve a la primera. */
  const [pagina, setPagina] = useState(0);
  /**
   * La grilla es mas ancha que su contenedor: hay columnas fuera de la vista.
   *
   * No es un detalle estetico. Con siete columnas —las que pide cargar un gasto
   * de expensas— en una pantalla de 1568 px se ven cuatro, y nada decia que
   * hubiera tres mas: se cargaba la linea creyendo que eso era todo el
   * formulario. Se mide en el DOM y no se supone por el ancho de la ventana,
   * porque el ancho que manda es el del contenedor (el formulario, la solapa),
   * no el de la pantalla.
   */
  const [contenedor, setContenedor] = useState<HTMLDivElement | null>(null);
  const [hayColumnasFuera, setHayColumnasFuera] = useState(false);

  useEffect(() => {
    if (contenedor === null) return;
    const medir = (): void => {
      setHayColumnasFuera(contenedor.scrollWidth - contenedor.clientWidth > 1);
    };
    medir();
    // Se vigilan los dos: el contenedor cambia con la ventana y con la barra
    // lateral; la tabla cambia cuando cambian las columnas o llega una fila mas
    // ancha. Con uno solo el aviso se quedaba pegado al primer render.
    const observador = new ResizeObserver(medir);
    observador.observe(contenedor);
    const tabla = contenedor.querySelector('table');
    if (tabla !== null) observador.observe(tabla);
    return () => {
      observador.disconnect();
    };
  }, [contenedor]);

  /**
   * Relacion generica: el chatter (`messages`, `followers`, `activities`).
   *
   * El backend la declara con `generic: true` y SIN `relation_field`, y a
   * proposito **no expone** `core.message` ni `core.activity` como modelos
   * propios: se leen colgados de su registro. Pedirles la metadata devuelve 404,
   * asi que no se pide. Antes se pedia igual —la consulta salia antes del early
   * return de mas abajo— y cada formulario que declara el chatter (`crm.lead`,
   * `inventory.picking`, `inventory.warehouse`) dejaba dos 404 en la consola.
   */
  const esGenerica = meta.generic === true || campoInverso === undefined;

  const hijo = useMetaQuery(esGenerica ? null : (relation ?? null));

  /** Los filtros que el padre declaro, resueltos contra la busqueda del hijo. */
  const filtrosDisponibles = useMemo(() => {
    const pedidos = opcionLista(meta, 'filters');
    if (pedidos.length === 0) return [];
    const declarados = hijo.data?.views.search?.filters ?? [];
    return pedidos
      .map((nombre) => declarados.find((filtro) => filtro.name === nombre))
      .filter((filtro): filtro is NonNullable<typeof filtro> => filtro !== undefined);
  }, [meta, hijo.data]);

  // Las líneas se piden con la misma clave que cualquier otra lista del modelo
  // hijo: así, cuando se crea o modifica una desde acá, la invalidación también
  // refresca la pantalla de ese modelo si el usuario la tiene abierta.
  //
  // El dominio del filtro elegido se CONCATENA al del padre (y por lo tanto se
  // combina con AND): filtrar acota lo que se ve de este registro, nunca puede
  // traer lineas de otro.
  const parametros = useMemo(() => {
    // El mismo dominio que `parametrosInicialesDeLaGrilla`: tienen que dar la
    // misma clave de caché para que lo pedido de antemano sirva.
    const delPadre: DomainNode[] = dominioDelPadre(meta, padreId);
    const elegido = filtrosDisponibles.find((filtro) => filtro.name === filtroActivo);
    const domain: Domain = elegido === undefined ? delPadre : [...delPadre, ...elegido.domain];
    // Lo que eligio el usuario gana; si no toco nada, el que declaro el padre.
    // Se manda el string DECLARADO tal cual (y no el reserializado) para no
    // perder un orden secundario del tipo `"-end_date,name"`.
    const order = serializeOrder(orden) ?? opcionTexto(meta, 'order');
    const base = { domain, limit: PAGINA, offset: pagina * PAGINA };
    return order !== undefined ? { ...base, order } : base;
  }, [campoInverso, padreId, filtrosDisponibles, filtroActivo, meta, orden, pagina]);

  const lineas = useRecordsQuery(
    relation ?? '',
    parametros,
    relation !== undefined && campoInverso !== undefined && !esAlta,
  );

  /**
   * Errores por campo que manda el backend en un 400, listos para pintar celdas.
   *
   * Solo se quedan los que nombran una columna de esta grilla: `non_field_errors`
   * habla del registro entero y ya viaja en el aviso flotante.
   */
  const erroresDeCampo = (error: unknown): Record<string, string> => {
    if (!(error instanceof ApiError)) return {};
    const salida: Record<string, string> = {};
    for (const [campo, mensajes] of Object.entries(error.fields)) {
      if (hijo.data?.fields[campo] === undefined) continue;
      salida[campo] = mensajes.join(' ');
    }
    return salida;
  };

  /** Clave estable de una fila para el mapa de avisos: existente o recien creada. */
  const claveDeAviso = (idLinea: number | null, clave: number | null): string =>
    idLinea !== null ? `linea-${idLinea}` : `nueva-${clave ?? 0}`;

  /** Retira el globo rojo que habia dejado esta misma fila, si es que hay uno. */
  const retirarAviso = (llave: string): void => {
    const idAviso = avisoDeError.current.get(llave);
    if (idAviso === undefined) return;
    avisoDeError.current.delete(llave);
    useToastStore.getState().dismiss(idAviso);
  };

  /** Muestra el error de la fila y se acuerda del aviso para poder retirarlo. */
  const avisarError = (llave: string, titulo: string, error: unknown): void => {
    // Primero se retira el anterior: reintentar y volver a fallar apilaba dos
    // globos identicos que despues habia que cerrar de a uno.
    retirarAviso(llave);
    avisoDeError.current.set(
      llave,
      notify.error(titulo, error instanceof Error ? error.message : undefined),
    );
  };

  const guardar = useMutation({
    mutationFn: async (entrada: { idLinea: number | null; clave: number | null; valores: Borrador }) => {
      const payload = entrada.valores as RecordPayload;
      if (entrada.idLinea === null) {
        return api.createRecord(relation as string, {
          ...payload,
          [campoInverso as string]: padreId,
        });
      }
      return api.updateRecord(relation as string, entrada.idLinea, payload);
    },
    onSuccess: (_datos, entrada) => {
      // La linea entro: el globo rojo del intento anterior ya no describe nada.
      retirarAviso(claveDeAviso(entrada.idLinea, entrada.clave));
      refrescarPadreEHijo();
    },
    onError: (error, entrada) => {
      const porCampo = erroresDeCampo(error);
      if (Object.keys(porCampo).length > 0) {
        if (entrada.idLinea !== null) {
          setErroresExistentes((actual) => ({ ...actual, [entrada.idLinea as number]: porCampo }));
        } else if (entrada.clave !== null) {
          setErroresNuevas((actual) => ({ ...actual, [entrada.clave as number]: porCampo }));
        }
        // La celda culpable se trae a la vista. Sin esto el 400 pintaba de rojo
        // una columna que estaba fuera del desplazamiento horizontal: el mensaje
        // existia, era bueno, y el usuario no lo veia nunca.
        const primero = Object.keys(porCampo)[0] as string;
        const idCelda =
          entrada.idLinea !== null
            ? `${id}-${entrada.idLinea}-${primero}`
            : `${id}-nueva-${entrada.clave as number}-${primero}`;
        // Tras el re-render que pinta el error, no antes: el `<p role="alert">`
        // todavia no existe cuando se ejecuta este callback.
        requestAnimationFrame(() => {
          traerCeldaALaVista(idCelda);
        });
      }
      avisarError(claveDeAviso(entrada.idLinea, entrada.clave), 'No se pudo guardar la línea', error);
    },
  });

  const borrar = useMutation({
    mutationFn: async (idLinea: number) => {
      if (hijo.data?.permissions.delete === true) return api.deleteRecord(relation as string, idLinea);
      return api.archiveRecord(relation as string, idLinea);
    },
    onSuccess: (_datos, idLinea) => {
      // La fila ya no esta: cualquier aviso que hubiera dejado habla de algo que
      // no se puede ni mirar.
      retirarAviso(claveDeAviso(idLinea, null));
      // Si era la ultima fila de la pagina, se retrocede una: quedarse mirando
      // una pagina que ya no existe parece que se borro todo.
      if ((lineas.data?.results.length ?? 0) <= 1) setPagina((actual) => Math.max(0, actual - 1));
      refrescarPadreEHijo();
    },
    onError: (error, idLinea) => {
      avisarError(claveDeAviso(idLinea, null), 'No se pudo quitar la línea', error);
    },
  });

  if (relation === undefined) {
    return (
      <p id={id} className="text-sm text-danger">
        El campo no declara a qué modelo apunta.
      </p>
    );
  }

  if (esGenerica) {
    // No hay un campo por el cual filtrar los hijos, asi que no se puede dibujar
    // la grilla. Y tampoco se ofrece un enlace: `core.message` y `core.activity`
    // no tienen pantalla propia —no estan expuestos como modelo— asi que mandar
    // ahi al usuario era prometerle una pantalla que devuelve 404.
    return (
      <p id={id} className="rounded-md border border-dashed border-line px-3 py-4 text-sm text-fg-muted">
        {meta.label} se registra automáticamente sobre este registro. Todavía no hay una vista para
        consultarlo desde acá.
      </p>
    );
  }

  // En un alta la consulta de lineas esta apagada, asi que se queda en
  // `isPending` para siempre: esperarla seria dejar la grilla cargando eterna.
  // La metadata del hijo si hace falta en los dos casos: sin ella no se sabe
  // que columnas dibujar.
  if (hijo.isPending || (!esAlta && lineas.isPending)) {
    return (
      <p id={id} className="flex items-center gap-2 py-3 text-sm text-fg-muted">
        <IconSpinner className="h-4 w-4" /> Cargando {meta.label.toLowerCase()}…
      </p>
    );
  }

  if (hijo.isError || lineas.isError) {
    const error = hijo.error ?? lineas.error;
    return (
      <p id={id} role="alert" className="text-sm text-danger">
        {error instanceof Error ? error.message : 'No se pudieron cargar las líneas.'}
      </p>
    );
  }

  const spec = hijo.data;
  const columnas = columnasDe(spec, campoInverso, opcionLista(meta, 'columns'));
  const etiquetas = etiquetasDeclaradas(meta);
  /**
   * El orden que hay que DIBUJAR en las cabeceras: el elegido o, si no se
   * eligio ninguno, el declarado por el padre. Sin esto la grilla llegaba
   * ordenada por vencimiento y no lo decia en ningun lado.
   */
  const ordenVisible = orden ?? parseOrder(opcionTexto(meta, 'order') ?? null);
  /**
   * Solo se puede ordenar por columnas que existen en la base: la API ordena
   * contra el `_meta` del modelo, nunca contra anotaciones al vuelo, y pedir
   * `?order=` de un campo no almacenado devuelve 400. Un encabezado que no se
   * puede ordenar se dibuja como texto, no como un boton que falla.
   */
  const ordenable = (nombre: string): boolean => spec.fields[nombre]?.store === true;
  const filas = lineas.data?.results ?? [];
  const total = lineas.data?.count ?? 0;
  /**
   * Columnas que el MODELO HIJO declara totalizables, acotadas a las que esta
   * grilla dibuja. No hace falta declararlas otra vez en las `options` del
   * padre: un segundo lugar donde decir lo mismo es un segundo lugar donde se
   * desincroniza.
   */
  const totalesDelHijo = (spec.views.list?.totals ?? []).filter(
    (nombre) => columnas.includes(nombre) && spec.fields[nombre] !== undefined,
  );
  const sumas = lineas.data?.totals;
  /** Donde va el rotulo «Total de N lineas»: la primera columna sin importe. */
  const primeraSinTotal = columnas.findIndex((nombre) => !totalesDelHijo.includes(nombre));
  const desde = total === 0 ? 0 : pagina * PAGINA + 1;
  const hasta = pagina * PAGINA + filas.length;
  const hayVariasPaginas = total > PAGINA;
  const soloLectura = readonly || meta.options?.['readonly'] === true;
  /**
   * Por que no se puede editar, si el padre lo declaro. `readonly` llega ya
   * resuelto desde `FormView`, que es quien evalua `options.readonly_domain`
   * contra el registro padre; aca solo se dice en castellano.
   */
  const motivoBloqueo =
    readonly && typeof meta.options?.['readonly_text'] === 'string'
      ? (meta.options['readonly_text'] as string)
      : null;
  const puedeEditar = !soloLectura && spec.permissions.write;
  const puedeCrear = !soloLectura && spec.permissions.create;
  const puedeQuitar = !soloLectura && (spec.permissions.delete || spec.permissions.write);

  /**
   * Por qué NO está el botón «Agregar línea».
   *
   * Tres causas distintas y una sola consecuencia visual —la grilla se ve
   * editable pero sin botón—, que es exactamente la forma del defecto: la de
   * `readonly_domain` ya tenía su cartel (`motivoBloqueo`, que ahora lo dibuja
   * el formulario una sola vez) y las otras dos no decían nada. La de «Ventas»
   * de un cliente es la segunda: la grilla la escribe la venta rápida, no una
   * persona, y eso no estaba escrito en ningún lado.
   *
   * `null` cuando el botón SÍ está: no hay nada que explicar.
   */
  /** Columnas de la grilla que el sistema calcula y por eso no se editan. */
  const columnasCalculadas = columnas
    .map((nombre) => spec.fields[nombre])
    .filter((metaCampo): metaCampo is NonNullable<typeof metaCampo> => metaCampo !== undefined)
    .filter((metaCampo) => metaCampo.readonly)
    .map((metaCampo) => metaCampo.label);

  const motivoSinAgregar: string | null = puedeCrear
    ? null
    : // El bloqueo por `readonly_domain` YA lo explica el formulario arriba, con
      // el mismo texto: repetirlo acá es el defecto de los carteles duplicados
      // que este trabajo vino a sacar (comprobante 255: cinco carteles, dos
      // idénticos). Los otros dos casos son los que no decían nada.
      motivoBloqueo !== null
      ? null
      : meta.options?.['readonly'] === true
        ? `${meta.label} se carga desde otra pantalla y no a mano: acá se consulta lo que quedó registrado.`
        : !spec.permissions.create
          ? `Tu usuario puede mirar ${meta.label.toLowerCase()} pero no agregar líneas nuevas.`
          : null;
  /**
   * Ancho de la ultima columna. Ver `ANCHO_ACCIONES_EDITABLE`: una grilla que se
   * edita lleva dos botones con texto y una de solo lectura, dos iconos.
   */
  const anchoDeAcciones =
    puedeEditar || puedeCrear || puedeQuitar ? ANCHO_ACCIONES_EDITABLE : ANCHO_ACCIONES_LECTURA;

  const cambiarExistente = (idLinea: number, campo: string, valor: FieldValue): void => {
    setBorradores((actual) => ({ ...actual, [idLinea]: { ...(actual[idLinea] ?? {}), [campo]: valor } }));
    // Tocar la celda borra su error: dejarla en rojo despues de corregirla hace
    // dudar de si se corrigio.
    setErroresExistentes((actual) => {
      const dela = actual[idLinea];
      if (dela === undefined || dela[campo] === undefined) return actual;
      const copia = { ...dela };
      delete copia[campo];
      return { ...actual, [idLinea]: copia };
    });
  };

  const cambiarNueva = (clave: number, campo: string, valor: FieldValue): void => {
    setNuevas((actual) =>
      actual.map((fila) => (fila.clave === clave ? { ...fila, valores: { ...fila.valores, [campo]: valor } } : fila)),
    );
    // Completar el campo borra su error: dejarlo en rojo despues de arreglarlo
    // hace dudar de si se arreglo.
    setErroresNuevas((actual) => {
      const dela = actual[clave];
      if (dela === undefined || dela[campo] === undefined) return actual;
      const copia = { ...dela };
      delete copia[campo];
      return { ...actual, [clave]: copia };
    });
  };

  const guardarExistente = (idLinea: number): void => {
    const borrador = borradores[idLinea];
    if (borrador === undefined) return;
    setGuardando(idLinea);
    guardar.mutate(
      { idLinea, clave: null, valores: borrador },
      {
        onSuccess: () => {
          setBorradores((actual) => {
            const copia = { ...actual };
            delete copia[idLinea];
            return copia;
          });
          setErroresExistentes((actual) => {
            const copia = { ...actual };
            delete copia[idLinea];
            return copia;
          });
        },
        onSettled: () => {
          setGuardando(null);
        },
      },
    );
  };

  const guardarNueva = (clave: number): void => {
    const fila = nuevas.find((entrada) => entrada.clave === clave);
    if (fila === undefined) return;

    // Se valida ANTES de mandar, con la metadata del hijo. El caso que importa
    // es el many2one obligatorio vacio: sin esto salia un POST que el servidor
    // rechazaba con 400 y el usuario veia el nombre tecnico del campo.
    const problemas: Record<string, string> = {};
    for (const nombre of columnas) {
      const metaCampo = spec.fields[nombre];
      if (metaCampo === undefined) continue;
      const mensaje = validateField(metaCampo, fila.valores[nombre] ?? null);
      if (mensaje !== null) problemas[nombre] = mensaje;
    }
    if (Object.keys(problemas).length > 0) {
      setErroresNuevas((actual) => ({ ...actual, [clave]: problemas }));
      const primero = Object.keys(problemas)[0] as string;
      const etiqueta = etiquetas[primero] ?? spec.fields[primero]?.label ?? primero;
      notify.warning('Falta completar la línea', `«${etiqueta}»: ${problemas[primero] ?? ''}`);
      traerCeldaALaVista(`${id}-nueva-${clave}-${primero}`);
      return;
    }
    setErroresNuevas((actual) => ({ ...actual, [clave]: {} }));

    // Padre sin guardar: la fila se suma a las de memoria y la escribe el POST
    // de «Crear», junto con la cabecera y en la misma transaccion. No hay viaje
    // al servidor que esperar, asi que tampoco hay estado de «guardando».
    if (esAlta) {
      onChange([...enMemoria, fila.valores] as FieldValue);
      setNuevas((actual) => actual.filter((entrada) => entrada.clave !== clave));
      setErroresNuevas((actual) => {
        const copia = { ...actual };
        delete copia[clave];
        return copia;
      });
      return;
    }

    setGuardando(-clave);
    guardar.mutate(
      { idLinea: null, clave, valores: fila.valores },
      {
        onSuccess: () => {
          setNuevas((actual) => actual.filter((entrada) => entrada.clave !== clave));
          setErroresNuevas((actual) => {
            const copia = { ...actual };
            delete copia[clave];
            return copia;
          });
        },
        onSettled: () => {
          setGuardando(null);
        },
      },
    );
  };

  /**
   * Corrige una celda de una fila ya cargada en memoria.
   *
   * Va directo al `value` del campo: no hay «Guardar» que apretar porque no hay
   * nada guardado todavia. El boton existiria solo para simular un viaje que no
   * ocurre.
   */
  const cambiarEnMemoria = (indice: number, campo: string, valor: FieldValue): void => {
    onChange(
      enMemoria.map((fila, posicion) =>
        posicion === indice ? { ...fila, [campo]: valor } : fila,
      ) as FieldValue,
    );
  };

  const quitarEnMemoria = (indice: number): void => {
    onChange(enMemoria.filter((_fila, posicion) => posicion !== indice) as FieldValue);
  };

  const celdaEditable = (
    metaCampo: FieldMeta,
    nombre: string,
    valor: FieldValue,
    alCambiar: (valor: FieldValue) => void,
    idCelda: string,
    error: string | null = null,
  ) => {
    const Widget = getFieldInput(metaCampo);
    return (
      <>
        <Widget
          id={idCelda}
          name={nombre}
          meta={metaCampo}
          value={valor}
          onChange={alCambiar}
          readonly={metaCampo.readonly}
          required={metaCampo.required}
          invalid={error !== null}
          {...(error === null ? {} : { describedBy: `${idCelda}-error` })}
          context={{ modelName: relation, recordId: null }}
        />
        {error !== null && (
          <p id={`${idCelda}-error`} role="alert" className="mt-1 text-xs text-danger">
            {error}
          </p>
        )}
      </>
    );
  };

  return (
    <div id={id} role="group" aria-labelledby={`${id}-label`} aria-describedby={describedBy}>
      {/*
        El `readonly_text` ya NO se dibuja acá: lo pinta `FormView`, una sola
        vez, arriba del formulario, junto con los motivos de los demás campos
        bloqueados. Dibujarlo en los dos lados era el defecto de los carteles
        duplicados —medido: cinco carteles en el comprobante 255, dos con texto
        idéntico; cuatro en el período de expensas 32, dos idénticos—. El ruido
        devalúa el único canal que ya funcionaba bien.
      */}

      {/*
        Que las filas todavia no estan en la base se DICE, y se dice con el
        boton que las va a escribir. Sin esto la grilla se ve igual que la de un
        registro guardado y no hay forma de saber que salir de la pantalla las
        pierde —el aviso de «cambios sin guardar» del formulario las cubre, pero
        recien al intentar salir—.
      */}
      {esAlta && enMemoria.length > 0 && (
        <p role="status" className="mb-2 rounded-lg border border-line bg-app-subtle px-3 py-2 text-sm text-fg-muted">
          {enMemoria.length === 1
            ? 'Hay 1 línea cargada y todavía sin guardar.'
            : `Hay ${enMemoria.length} líneas cargadas y todavía sin guardar.`}{' '}
          Se guardan junto con el registro cuando apretás «Crear»: entra todo o no entra nada.
        </p>
      )}

      {/* Los chips de filtro no se dibujan en un alta: filtran la consulta al
          servidor, que en un registro que todavia no existe esta apagada, asi
          que serian botones que no hacen nada. */}
      {!esAlta && filtrosDisponibles.length > 0 && (
        <div
          role="group"
          aria-label={`Filtrar ${meta.label.toLowerCase()}`}
          className="mb-2 flex flex-wrap items-center gap-1 rounded-lg border border-line bg-surface p-0.5"
        >
          {[{ name: '', label: 'Todos' }, ...filtrosDisponibles].map((filtro) => {
            const nombre = filtro.name === '' ? null : filtro.name;
            const activo = filtroActivo === nombre;
            return (
              <button
                key={filtro.name === '' ? '__todos' : filtro.name}
                type="button"
                aria-pressed={activo}
                onClick={() => {
                  setFiltroActivo(nombre);
                  // Otro filtro son otras lineas: quedarse en la pagina 3 de la
                  // lista anterior es mostrar una pagina vacia sin motivo.
                  setPagina(0);
                }}
                className={
                  'rounded-md px-2.5 py-1 text-xs font-medium transition-colors ' +
                  (activo ? 'bg-accent-soft text-accent' : 'text-fg-muted hover:bg-surface-raised')
                }
              >
                {filtro.label}
              </button>
            );
          })}
        </div>
      )}

      {/*
        `relative`: los textos para lectores de pantalla de esta grilla —el
        `<caption class="sr-only">` y el «Acciones» de la ultima cabecera— son
        `position: absolute`, y sin un ancestro posicionado su bloque contenedor
        es el viewport: quedan FUERA de este contenedor con `overflow`, no se
        recortan, y hacen que la PAGINA ENTERA se desplace en horizontal cuando
        la grilla es mas ancha que la pantalla. Con `relative` el desplazamiento
        pasa a ser el de la grilla, que es el que corresponde.
      */}
      <div ref={setContenedor} className="relative overflow-x-auto rounded-lg border border-line">
        {/*
          `table-fixed`: el ancho lo decide la declaracion de columnas y no el
          contenido. Es lo que hace cumplible la regla por tipo de dato —ver
          `ANCHO_DE_COLUMNA`— y ademas vuelve estable la grilla: hoy la columna
          «Importe» no cambia de ancho porque una fila traiga un proveedor de
          nombre largo. `min-w-[36rem]` sigue mandando en pantallas chicas, donde
          la grilla se desplaza en horizontal.
        */}
        <table className="w-full min-w-[36rem] table-fixed border-collapse text-sm">
          <caption className="sr-only">
            {meta.label}
            {ordenVisible !== null &&
              `, ordenado por ${
                etiquetas[ordenVisible.field] ?? spec.fields[ordenVisible.field]?.label ?? ordenVisible.field
              }`}
          </caption>
          <thead>
            <tr className="border-b border-line bg-app-subtle text-left">
              {columnas.map((nombre) => {
                const titulo = etiquetas[nombre] ?? spec.fields[nombre]?.label ?? nombre;
                const sentido =
                  ordenVisible === null || ordenVisible.field !== nombre
                    ? 'none'
                    : ordenVisible.desc
                      ? 'descending'
                      : 'ascending';
                const activa = sentido !== 'none';
                // Ancho por TIPO de dato (ver `anchoDeColumna`): la plata y las
                // fechas entran completas; el texto se lleva lo que sobra.
                const estilo = { width: anchoDeColumna(spec.fields[nombre]) };
                if (!ordenable(nombre)) {
                  return (
                    <th
                      key={nombre}
                      scope="col"
                      className="px-3 py-2 font-medium text-fg-muted"
                      style={estilo}
                    >
                      {titulo}
                    </th>
                  );
                }
                return (
                  <th
                    key={nombre}
                    scope="col"
                    aria-sort={sentido}
                    className="px-3 py-2 font-medium text-fg-muted"
                    style={estilo}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setOrden((actual) => toggleOrder(actual ?? ordenVisible, nombre));
                        setPagina(0);
                      }}
                      className={
                        'inline-flex items-center gap-1 rounded transition-colors hover:text-fg ' +
                        (activa ? 'text-fg' : '')
                      }
                    >
                      {titulo}
                      <span aria-hidden="true" className={'text-2xs ' + (activa ? '' : 'opacity-0')}>
                        {sentido === 'descending' ? '▼' : '▲'}
                      </span>
                    </button>
                  </th>
                );
              })}
              <th
                scope="col"
                className={`${CLASE_ACCIONES} border-l border-line bg-app-subtle px-3 py-2`}
                style={{ width: anchoDeAcciones }}
              >
                <span className="sr-only">Acciones</span>
              </th>
            </tr>
          </thead>

          <tbody>
            {filas.map((fila: RecordData) => {
              const borrador = borradores[fila.id];
              const sucia = borrador !== undefined && Object.keys(borrador).length > 0;
              return (
                <tr key={fila.id} className="border-b border-line last:border-0 align-top">
                  {columnas.map((nombre) => {
                    const metaCampo = spec.fields[nombre];
                    if (metaCampo === undefined) return <td key={nombre} className="px-3 py-2" />;
                    const valor = borrador !== undefined && nombre in borrador ? borrador[nombre] : fila[nombre];
                    return (
                      <td key={nombre} className="px-3 py-1.5">
                        {puedeEditar && !metaCampo.readonly ? (
                          celdaEditable(
                            metaCampo,
                            nombre,
                            valor ?? null,
                            (nuevo) => {
                              cambiarExistente(fila.id, nombre, nuevo);
                            },
                            `${id}-${fila.id}-${nombre}`,
                            erroresExistentes[fila.id]?.[nombre] ?? null,
                          )
                        ) : (
                          // `overflow-hidden`: con `table-layout: fixed` la celda
                          // tiene un ancho dado, y sin esto un texto largo se sale
                          // por encima de la columna de al lado en vez de cortarse
                          // con puntos suspensivos.
                          <span className="block overflow-hidden py-1.5">
                            <FieldDisplay meta={metaCampo} value={fila[nombre] ?? null} compact />
                          </span>
                        )}
                      </td>
                    );
                  })}

                  <td className={`${CLASE_ACCIONES} border-l border-line bg-surface px-3 py-1.5`}>
                    {/* `flex-wrap`: la garantia de que los botones NUNCA se
                        dibujen sobre la columna de al lado. El ancho de la
                        columna ya alcanza (ver `ANCHO_ACCIONES_EDITABLE`); si
                        alguna vez no alcanzara —una etiqueta mas larga, un zoom
                        del navegador— los botones bajan a una segunda linea en
                        vez de invadir la celda del importe. */}
                    <div className="flex flex-wrap items-center justify-end gap-1">
                      {sucia && (
                        <Button
                          size="sm"
                          variant="primary"
                          loading={guardando === fila.id}
                          onClick={() => {
                            guardarExistente(fila.id);
                          }}
                        >
                          Guardar
                        </Button>
                      )}
                      {sucia && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setBorradores((actual) => {
                              const copia = { ...actual };
                              delete copia[fila.id];
                              return copia;
                            });
                          }}
                        >
                          Descartar
                        </Button>
                      )}
                      {!sucia && (
                        <Link
                          to={recordPath(relation, fila.id)}
                          className="grid h-8 w-8 place-items-center rounded-md text-fg-muted transition-colors hover:bg-fg/5 hover:text-fg"
                          aria-label={`Abrir ${fila.display_name}`}
                          title="Abrir"
                        >
                          <IconChevronRight className="h-4 w-4" />
                        </Link>
                      )}
                      {!sucia && puedeQuitar && (
                        <button
                          type="button"
                          onClick={() => {
                            borrar.mutate(fila.id);
                          }}
                          className="grid h-8 w-8 place-items-center rounded-md text-fg-muted transition-colors hover:bg-danger-soft hover:text-danger"
                          aria-label={`Quitar ${fila.display_name}`}
                          title={spec.permissions.delete ? 'Eliminar' : 'Archivar'}
                        >
                          <IconClose className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}

            {/*
              Las filas cargadas antes de guardar el registro. Se ven como
              cualquier otra fila —se corrigen celda por celda y se quitan con la
              X— pero todavia no existen en la base: las escribe «Crear».
            */}
            {enMemoria.map((fila, indice) => (
              <tr key={`memoria-${indice}`} className="border-b border-line last:border-0 align-top">
                {columnas.map((nombre) => {
                  const metaCampo = spec.fields[nombre];
                  if (metaCampo === undefined) return <td key={nombre} className="px-3 py-2" />;
                  return (
                    <td key={nombre} className="px-3 py-1.5">
                      {/* El permiso que corresponde acá es `create` y no
                          `write`: la fila todavía no existe, así que lo que se
                          está haciendo es darla de alta. */}
                      {puedeCrear && !metaCampo.readonly ? (
                        celdaEditable(
                          metaCampo,
                          nombre,
                          fila[nombre] ?? null,
                          (nuevo) => {
                            cambiarEnMemoria(indice, nombre, nuevo);
                          },
                          `${id}-memoria-${indice}-${nombre}`,
                        )
                      ) : (
                        <span className="block overflow-hidden py-1.5">
                          <FieldDisplay meta={metaCampo} value={fila[nombre] ?? null} compact />
                        </span>
                      )}
                    </td>
                  );
                })}
                <td className={`${CLASE_ACCIONES} border-l border-line bg-surface px-3 py-1.5`}>
                  <div className="flex flex-wrap items-center justify-end gap-1">
                    {/* No hay flecha «Abrir»: la fila todavia no tiene pantalla
                        propia porque todavia no tiene id. Y quitarla no borra
                        nada de la base, asi que alcanza con poder cargarla. */}
                    {puedeCrear && (
                      <button
                        type="button"
                        onClick={() => {
                          quitarEnMemoria(indice);
                        }}
                        className="grid h-8 w-8 place-items-center rounded-md text-fg-muted transition-colors hover:bg-danger-soft hover:text-danger"
                        aria-label={`Quitar la línea ${indice + 1}`}
                        title="Quitar"
                      >
                        <IconClose className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}

            {nuevas.map((fila) => (
              <tr key={`nueva-${fila.clave}`} className="border-b border-line bg-accent-soft/40 last:border-0 align-top">
                {columnas.map((nombre) => {
                  const metaCampo = spec.fields[nombre];
                  if (metaCampo === undefined) return <td key={nombre} className="px-3 py-2" />;
                  return (
                    <td key={nombre} className="px-3 py-1.5">
                      {/*
                        La etiqueta de la columna, repetida DENTRO de la celda que
                        se esta cargando. No es redundante con el encabezado: la
                        fila de alta es la ultima de la grilla —con 70 cargos, el
                        encabezado quedo lejos hacia arriba— y ademas la grilla se
                        desplaza en horizontal, asi que el que escribe puede tener
                        el encabezado fuera de la vista en los dos ejes. Sin esto
                        se carga a ciegas: el detalle terminaba tipeado adentro de
                        «Grupo de unidades» y el error saltaba despues.
                        `aria-hidden`: para un lector de pantalla la celda ya esta
                        asociada a su `<th scope="col">`; esto es ayuda VISUAL y
                        repetirla seria leer dos veces el mismo nombre.
                      */}
                      <span
                        aria-hidden="true"
                        className="mb-1 block truncate text-2xs font-medium uppercase tracking-wide text-fg-muted"
                      >
                        {etiquetas[nombre] ?? metaCampo.label ?? nombre}
                        {metaCampo.required && <span className="text-danger"> *</span>}
                      </span>
                      {celdaEditable(
                        metaCampo,
                        nombre,
                        fila.valores[nombre] ?? null,
                        (nuevo) => {
                          cambiarNueva(fila.clave, nombre, nuevo);
                        },
                        `${id}-nueva-${fila.clave}-${nombre}`,
                        erroresNuevas[fila.clave]?.[nombre] ?? null,
                      )}
                    </td>
                  );
                })}
                {/* `bg-accent-soft` opaco y no el `/40` de la fila: una celda
                    anclada tiene que TAPAR lo que le pasa por debajo. */}
                <td className={`${CLASE_ACCIONES} border-l border-line bg-accent-soft px-3 py-1.5`}>
                  <div className="flex flex-wrap items-center justify-end gap-1">
                    <Button
                      size="sm"
                      variant="primary"
                      loading={guardando === -fila.clave}
                      onClick={() => {
                        guardarNueva(fila.clave);
                      }}
                    >
                      Agregar
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setNuevas((actual) => actual.filter((entrada) => entrada.clave !== fila.clave));
                        setErroresNuevas((actual) => {
                          const copia = { ...actual };
                          delete copia[fila.clave];
                          return copia;
                        });
                      }}
                    >
                      Cancelar
                    </Button>
                  </div>
                </td>
              </tr>
            ))}

            {filas.length === 0 && nuevas.length === 0 && enMemoria.length === 0 && (
              <tr>
                <td colSpan={columnas.length + 1} className="px-3 py-6 text-center text-sm text-fg-muted">
                  {filtroActivo !== null
                    ? 'Nada con ese filtro.'
                    : (opcionTexto(meta, 'empty_text') ??
                      `Todavía no hay ${meta.label.toLowerCase()}.`)}
                </td>
              </tr>
            )}
          </tbody>

          {/*
            El pie con los totales. Sale del modelo HIJO (`views.list.totals`) y
            lo suma el servidor sobre todas las lineas de este padre, no sobre la
            pagina: en la ficha de un cliente con 120 ventas el total tiene que
            ser el de las 120 y no el de las 100 que entraron.

            Es lo que hace que la ficha del cliente conteste «¿cuanto le
            vendimos?» sin ir a otra pantalla. No hay una linea especifica de
            ventas aca: cualquier grilla cuyo modelo declare totales lo gana.
          */}
          {totalesDelHijo.length > 0 && sumas !== undefined && filas.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-line bg-app-subtle font-medium">
                {columnas.map((nombre, indice) => {
                  const metaCampo = spec.fields[nombre];
                  if (metaCampo !== undefined && totalesDelHijo.includes(nombre)) {
                    return (
                      <td key={nombre} className="px-3 py-2 text-right tabular-nums">
                        <FieldDisplay meta={metaCampo} value={sumas[nombre] ?? null} compact />
                      </td>
                    );
                  }
                  if (indice === primeraSinTotal) {
                    return (
                      <td key={nombre} className="px-3 py-2 text-fg-muted">
                        {total === 1 ? 'Total de 1 línea' : `Total de ${total} líneas`}
                      </td>
                    );
                  }
                  return <td key={nombre} className="px-3 py-2" />;
                })}
                <td className={`${CLASE_ACCIONES} border-l border-line bg-app-subtle px-3 py-2`} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/*
        Que la grilla no entre entera es un hecho legitimo —siete columnas no
        caben en 1100 px— pero callarlo no lo es: se veian cuatro columnas y nada
        indicaba que hubiera tres mas, asi que la linea se daba de alta creyendo
        que eso era todo el formulario. Se dice cuantas son y como llegar a ellas.
      */}
      {hayColumnasFuera && (
        <p role="status" className="mt-2 text-xs text-fg-muted">
          Las {columnas.length} columnas no entran a lo ancho: desplazá la grilla
          en horizontal para ver el resto, o abrila con «Ver todas en su pantalla».
        </p>
      )}

      {/*
        Las celdas calculadas se dibujan como texto en medio de una fila de
        inputs y hasta acá no lo explicaba nada: el mismo patrón del botón que se
        apaga, un nivel más abajo. Se dice una vez para la grilla entera y no
        celda por celda, que sobre veinte filas sería el mismo texto veinte
        veces.
      */}
      {puedeEditar && columnasCalculadas.length > 0 && (
        <p role="status" className="mt-2 text-xs text-fg-muted">
          {columnasCalculadas.length === 1
            ? `«${columnasCalculadas[0] as string}» la calcula el sistema a partir de las demás: se ve, no se escribe.`
            : `${columnasCalculadas.map((etiqueta) => `«${etiqueta}»`).join(', ')} las calcula el sistema a partir de las demás: se ven, no se escriben.`}
        </p>
      )}

      {puedeCrear ? (
        <div className="mt-2">
          <Button
            size="sm"
            onClick={() => {
              setNuevas((actual) => [
                ...actual,
                { clave: Date.now() + actual.length, valores: valoresDeFilaNueva(spec, columnas) },
              ]);
            }}
          >
            Agregar línea
          </Button>
        </div>
      ) : (
        motivoSinAgregar !== null && (
          <div className="mt-2">
            <AvisoDeBloqueo motivo={motivoSinAgregar} />
          </div>
        )
      )}

      {/*
        Paginado. Antes la grilla pedia 100 lineas fijas y no habia forma de ver
        la 101: un consorcio de 60 unidades por 6 rubros son 360 cargos, se veian
        los primeros 100 y NADA lo decia. Ahora el pie declara siempre el rango y
        el total —tambien cuando entra todo en una pagina, que es la unica forma
        de que el numero sea informacion y no una alarma—, y los dos botones
        recorren el resto sin salir del formulario.
      */}
      {total > 0 && (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-fg-muted" role="status">
            {total <= PAGINA
              ? `${total} ${total === 1 ? 'línea' : 'líneas'}.`
              : `Se muestran ${desde}–${hasta} de ${total}.`}{' '}
            <Link
              to={`/m/${encodeURIComponent(relation)}`}
              className="text-accent hover:underline"
            >
              Ver todas en su pantalla
            </Link>
            .
          </p>
          {hayVariasPaginas && (
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="secondary"
                disabled={pagina === 0}
                onClick={() => {
                  setPagina((actual) => Math.max(0, actual - 1));
                }}
              >
                Anteriores
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={hasta >= total}
                onClick={() => {
                  setPagina((actual) => actual + 1);
                }}
              >
                Siguientes
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
